import React, { useState, useRef, useEffect } from 'react';
import {
    CellMeasurer,
    CellMeasurerCache,
    createMasonryCellPositioner,
    Masonry,
    WindowScroller,
    AutoSizer,
} from 'react-virtualized';
import { Popover } from 'antd'
import { SimpleImg } from 'react-simple-img';
import './index.less';

const cache = new CellMeasurerCache({
    defaultHeight: 250,
    defaultWidth: 300,
    fixedWidth: true,
});
const cellPositioner = createMasonryCellPositioner({
    cellMeasurerCache: cache,
    columnCount: 5,
    columnWidth: 300,
    spacer: 10,
});

const imgSuffix = (w) => {
    return `?x-oss-process=image/auto-orient,1/resize,m_lfit,w_${Math.floor(
        w,
    )}/quality,q_100/format,webp`
};

const cellRenderer = ({
    index,
    parent,
    style,
    list,
    key,
    setShowButtons,
    onChange,
    onVisibleChange,
    popData
}) => {
    const v = list[index];
    return v ? (
        <CellMeasurer cache={cache} index={index} key={key} parent={parent}>
            <Popover onVisibleChange={(visible) => visible && onVisibleChange(v.insId)} arrowPointAtCenter={true} content={
                <div className="autoResponsiveItemPopover">
                    {/* <p>算法排序：第{v.searchOrder}层</p> */}
                    <p>图片得分：{popData.score ?? '无'}</p>
                    <p>ID：{popData.insId}</p>
                    <p style={{ whiteSpace: 'pre-wrap' }}>标签：{popData.tags || '无'}</p>
                    <p style={{ wordBreak: 'break-all' }}>原始标题：{popData.titleOrigin || '无'}</p>
                    <p>来源网站：{popData.website}</p>
                    <p>入库时间：{popData.updateTime}</p>
                    <p>原尺寸：{popData.size}</p>
                    <p style={{ wordBreak: 'break-all' }}>图片url：{popData.url}</p>
                </div>
            }>
                <li
                    className="autoResponsiveItem"
                    style={{ ...style, ...v.style }}
                    onMouseOver={() => setShowButtons(index)}
                    onMouseLeave={() => setShowButtons(-1)}
                    onClick={() => !v.delete && onChange(index, v)}
                >
                    {v.checked || v.delete
                        ? <div
                            className="autoResponsiveImgMask"
                            style={{
                                width: v.style.width,
                                height: v.style.height,
                                color: v.delete ? '#ff4d4f' : '#fff'
                            }}
                        >{v.delete ? '已删除' : '已选择'}</div>
                        : null}
                    <SimpleImg
                        width={v.style.width}
                        height={v.style.height}
                        className="autoResponsiveCover"
                        importance={'low'}
                        animationDuration={0.1}
                        src={(v.imgMid || v.imgSmall) + imgSuffix(v.style.width)}
                    />
                </li>
            </Popover>
        </CellMeasurer>
    ) : null;
};

export default (props) => {
    const [showList, setShowList] = useState([]);
    const [showButtons, setShowButtons] = useState(-1);
    const {
        list,
        itemWidth,
        containerWidth,
        noButtons,
        downloadTag,
        tips,
        isDesc,
        noFeedBack,
        columnCount,
        diffHeight = 0,
        needResize,
        onChange,
        onVisibleChange,
        popData
    } = props;

    const masonryRef = useRef(null);
    const cacheRef = useRef(cache);
    const cellPositionerRef = useRef(cellPositioner);
    const columnCountRef = useRef(columnCount);
    const [spacer, setSpacer] = useState(10);
    const widthRef = useRef(containerWidth);
    const heightRef = useRef(100);
    const scrollTopRef = useRef(0);

    useEffect(() => {
        setSpacer((containerWidth - itemWidth * columnCount) / (columnCount - 1));
    }, [itemWidth, containerWidth]);

    useEffect(() => {
        const newList = list.map((v, i) => {
            let height = ((v.heightMid || v.heightSmall) / (v.widthMid || v.widthSmall)) * itemWidth;
            let style = {
                width: itemWidth,
                height: height,
            };
            return {
                ...v,
                height,
                style,
            };
        });
        setShowList(newList);
        if (needResize) {
            cellPositionerRef.current.reset({
                columnCount: columnCount,
                columnWidth: itemWidth,
                spacer: spacer,
            });
            cacheRef.current.clearAll();
            masonryRef.current.clearCellPositions();
        }
    }, [list]);

    useEffect(() => {
        cellPositionerRef.current.reset({
            columnCount: columnCount,
            columnWidth: itemWidth,
            spacer: spacer,
        });
        cacheRef.current.clearAll();
        masonryRef.current.clearCellPositions();
    }, [spacer]);

    const _calculateColumnCount = () => {
        const calcColumn = widthRef.current / (itemWidth + spacer);
        columnCountRef.current =
            calcColumn + 0.2 > columnCount ? columnCount : Math.floor(calcColumn);
    };

    const _resetCellPositioner = () => {
        cellPositionerRef.current.reset({
            columnCount: columnCountRef.current,
            columnWidth: itemWidth,
            spacer: spacer,
        });
    };

    const onResize = ({ width }) => {
        widthRef.current = width;
        _calculateColumnCount();
        _resetCellPositioner();
        masonryRef.current.recomputeCellPositions();
    };

    return (
        <WindowScroller scrollingResetTimeInterval={0}>
            {({ height, scrollTop }) => {
                heightRef.current = height;
                scrollTopRef.current = scrollTop;
                return (
                    <AutoSizer
                        disableHeight
                        overscanByPixels={50}
                        height={heightRef.current}
                        scrollTop={scrollTopRef.current}
                        onResize={onResize}
                    >
                        {({ width }) => {
                            widthRef.current = width;
                            return (
                                <Masonry
                                    ref={masonryRef}
                                    autoHeight
                                    width={width}
                                    height={heightRef.current + 600}
                                    scrollTop={scrollTopRef.current - diffHeight}
                                    overscanByPixels={20}
                                    cellCount={showList.length}
                                    cellMeasurerCache={cacheRef.current}
                                    cellPositioner={cellPositionerRef.current}
                                    cellRenderer={(params) => {
                                        return cellRenderer({
                                            ...params,
                                            list: showList,
                                            noButtons,
                                            showButtons,
                                            downloadTag,
                                            tips,
                                            noFeedBack,
                                            itemWidth,
                                            isDesc,
                                            setShowButtons,
                                            onChange,
                                            onVisibleChange,
                                            popData
                                        });
                                    }}
                                />
                            );
                        }}
                    </AutoSizer>
                );
            }}
        </WindowScroller>
    );
};
